/**
 * Iter133 — Super-Admin Abo & Umsatz Übersicht
 *
 * Zeigt MRR/ARR, Plan-Verteilung, Trials, überfällige Zahlungen, Churn.
 * Datenquelle: GET /api/admin/billing-overview (SA-only).
 */
import React, { useEffect, useState } from "react";
import { apiClient } from "@/lib/apiClient";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Loader2, TrendingUp, DollarSign, Users, AlertTriangle, Percent, ArrowUpRight, ArrowDownRight, Sparkles } from "lucide-react";

function fmtCHF(v) {
  if (v === null || v === undefined) return "—";
  return new Intl.NumberFormat("de-CH", { style: "currency", currency: "CHF", maximumFractionDigits: 0 }).format(v);
}

function KPICard({ icon: Icon, label, value, hint, color = "slate", testid }) {
  const colorMap = {
    emerald: "bg-emerald-50 text-emerald-700 border-emerald-200",
    indigo: "bg-indigo-50 text-indigo-700 border-indigo-200",
    amber: "bg-amber-50 text-amber-700 border-amber-200",
    rose: "bg-rose-50 text-rose-700 border-rose-200",
    slate: "bg-slate-50 text-slate-700 border-slate-200",
  };
  return (
    <Card className={`p-4 border ${colorMap[color]}`} data-testid={testid}>
      <div className="flex items-start justify-between">
        <div className={`h-9 w-9 rounded-lg flex items-center justify-center border ${colorMap[color]}`}>
          <Icon className="h-5 w-5" />
        </div>
      </div>
      <div className="mt-3 text-2xl font-bold text-slate-900" data-testid={`${testid}-value`}>{value}</div>
      <div className="text-xs font-medium text-slate-600 mt-0.5">{label}</div>
      {hint && <div className="text-[11px] text-slate-500 mt-1">{hint}</div>}
    </Card>
  );
}

export default function BillingOverviewCard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const r = await apiClient.get("/admin/billing-overview");
        setData(r.data);
      } catch (e) {
        toast.error(e.response?.data?.detail || "Abo-Übersicht konnte nicht geladen werden");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) {
    return (
      <Card className="p-10 flex items-center justify-center text-slate-500">
        <Loader2 className="h-5 w-5 animate-spin mr-2" /> Lade Abo-Metriken …
      </Card>
    );
  }
  if (!data) return null;

  return (
    <div className="space-y-5" data-testid="sa-billing-overview">
      {/* KPI-Reihe */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <KPICard
          icon={TrendingUp}
          label="MRR (Monthly Recurring Revenue)"
          value={fmtCHF(data.mrr_chf)}
          hint={`aus ${data.active_paying_companies} zahlenden Firmen`}
          color="emerald"
          testid="sa-mrr"
        />
        <KPICard
          icon={DollarSign}
          label="ARR (Annual Recurring Revenue)"
          value={fmtCHF(data.arr_chf)}
          hint="MRR × 12"
          color="indigo"
          testid="sa-arr"
        />
        <KPICard
          icon={Sparkles}
          label="Umsatz (letzte 30 Tage)"
          value={fmtCHF(data.revenue_30d_chf)}
          hint={`12 Monate: ${fmtCHF(data.revenue_12m_chf)}`}
          color="emerald"
          testid="sa-revenue-30d"
        />
        <KPICard
          icon={Percent}
          label="Trial-Konversion"
          value={`${data.trial_conversion_percent}%`}
          hint={`${data.trial_companies} aktive Trials`}
          color="amber"
          testid="sa-trial-conversion"
        />
      </div>

      {/* Zweite Reihe: Firmen-Metriken + Churn */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <KPICard
          icon={Users}
          label="Firmen (aktiv)"
          value={data.total_companies}
          hint={`davon zahlend: ${data.active_paying_companies}`}
          color="slate"
          testid="sa-total-companies"
        />
        <KPICard
          icon={ArrowUpRight}
          label="Neu in 30d (Trials)"
          value={data.trial_companies}
          hint="Free-Plan, jünger als 30 Tage"
          color="indigo"
          testid="sa-new-trials"
        />
        <KPICard
          icon={ArrowDownRight}
          label="Churn (30 Tage)"
          value={data.churned_30d}
          hint={`Rate: ${data.churn_rate_percent}%`}
          color="rose"
          testid="sa-churn"
        />
        <KPICard
          icon={AlertTriangle}
          label="Überfällige Zahlungen"
          value={data.overdue.length}
          hint="paid_until abgelaufen"
          color="rose"
          testid="sa-overdue-count"
        />
      </div>

      {/* Plan-Verteilung */}
      <Card className="p-5" data-testid="sa-plan-breakdown">
        <h3 className="text-sm font-semibold text-slate-900 mb-3">Plan-Verteilung</h3>
        <div className="space-y-2">
          {data.plan_breakdown.map((p) => {
            const totalWithFree = data.plan_breakdown.reduce((a, b) => a + b.company_count, 0);
            const pct = totalWithFree > 0 ? (p.company_count / totalWithFree) * 100 : 0;
            const barColor = p.plan_id === "free" ? "bg-slate-300" :
                             p.plan_id === "starter" ? "bg-indigo-500" :
                             p.plan_id === "pro" ? "bg-emerald-500" : "bg-violet-500";
            return (
              <div key={p.plan_id} className="flex items-center gap-3" data-testid={`sa-plan-${p.plan_id}`}>
                <div className="w-24 text-sm font-medium text-slate-800">{p.plan_name}</div>
                <div className="flex-1 h-6 bg-slate-100 rounded relative overflow-hidden">
                  <div
                    className={`h-full ${barColor} transition-all`}
                    style={{ width: `${pct}%` }}
                  />
                  <span className="absolute inset-0 flex items-center px-2 text-xs font-medium text-slate-800">
                    {p.company_count} Firm{p.company_count === 1 ? "a" : "en"} · {p.price_chf > 0 ? `${p.price_chf} CHF/Mt` : "kostenlos"}
                  </span>
                </div>
                <div className="w-24 text-right text-sm font-mono text-slate-700">
                  {p.mrr_contribution > 0 ? fmtCHF(p.mrr_contribution) : "—"}
                </div>
              </div>
            );
          })}
        </div>
      </Card>

      {/* Überfällige Zahlungen */}
      {data.overdue.length > 0 && (
        <Card className="p-5 border-rose-200 bg-rose-50" data-testid="sa-overdue-list">
          <h3 className="text-sm font-semibold text-rose-900 mb-3 flex items-center gap-1.5">
            <AlertTriangle className="h-4 w-4" /> Überfällige Zahlungen ({data.overdue.length})
          </h3>
          <div className="space-y-1.5">
            {data.overdue.map((c) => (
              <div key={c.company_id} className="flex items-center gap-3 text-sm bg-white rounded px-3 py-2 border border-rose-200">
                <div className="flex-1">
                  <span className="font-medium text-slate-900">{c.name}</span>
                  {c.owner_email && <span className="text-slate-500 ml-2 text-xs">({c.owner_email})</span>}
                </div>
                <Badge className="bg-rose-100 text-rose-800">{c.plan_id}</Badge>
                <span className="text-xs text-rose-700 font-medium">{c.days_overdue} Tage überfällig</span>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Aktuelle Trials */}
      {data.recent_trials.length > 0 && (
        <Card className="p-5" data-testid="sa-recent-trials">
          <h3 className="text-sm font-semibold text-slate-900 mb-3">Aktuelle Trials ({data.recent_trials.length})</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {data.recent_trials.map((t) => (
              <div key={t.company_id} className="flex items-center gap-2 text-sm bg-slate-50 rounded px-3 py-2 border border-slate-200">
                <div className="flex-1 truncate">
                  <span className="font-medium text-slate-900">{t.name}</span>
                  {t.owner_email && <div className="text-xs text-slate-500 truncate">{t.owner_email}</div>}
                </div>
                <Badge variant="outline" className="text-xs">
                  Tag {t.days_since_signup}/30
                </Badge>
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}
